import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CustomerSubscriptionApiService } from '@app/core/api/customer-subscription-api.service';
import { CustomerSummary } from '@app/core/models/common.models';
import { AdspertPlanType, Customer, CustomerSubscription } from '@app/core/models/customer.models';
import { valueToCamelCase } from '@app/core/utilities/string.utilities';
import { environment } from '@env/environment';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { User } from '../models/user.models';
import { LoggingService } from '../services/error-logging.service';

@Injectable()
export class SignupAPI {
  private _store: any = null;
  get store(): any {
    return this._store;
  }
  set store(value: any) {
    this._store = value;
  }
  public userInfo = null;
  public isNewAccountSignup = false;
  public isCustomerSignup = true;
  private uri = {
    customer: `${location.origin}/signup/customer`,
    account: `${location.origin}/signup/account`,
  };
  get resourceUrl(): string {
    return this.isCustomerSignup ? this.uri.customer : this.uri.account;
  }
  constructor(
    private http: HttpClient,
    private logger: LoggingService,
    private cs: CustomerSubscriptionApiService,
  ) {}

  getUserInfo(): Observable<User> {
    return this.http.get(`${location.origin}/${environment.app.resources.dashboard}users/0`).pipe(
      map(
        (response: any) =>
          ({
            userId: response.data.user_id,
            email: response.data.email,
            firstName: response.data.first_name || '',
            lastName: response.data.last_name || '',
            lang: !response.data.lang ? 'en' : response.data.lang.toLowerCase(),
          }) as User,
      ),
      catchError((error: any) => {
        return throwError({ msg: 'Could not fetch user details', error });
      }),
    );
  }
  updateUserInfo(modifiedUserInfo: {}): Observable<boolean> {
    return this.http.patch(`${location.origin}/${environment.app.resources.dashboard}users/0`, modifiedUserInfo).pipe(
      map(() => {
        this.userInfo = Object.assign(this.userInfo, modifiedUserInfo);
        return true;
      }),
      catchError((error: any) => {
        this.logger.logError('Updating user details failed', error, 'error');
        return throwError('Updating user details failed');
      }),
    );
  }

  getData(): Observable<any> {
    let $ = this.http.get(this.resourceUrl);
    // $ = of({
    //   status: 200,
    //   message: 'OK',
    //   data: {
    //     signup_id: 'bPXdjpAzmweyZNuED95MfC',
    //     type: 'ACCOUNT',
    //     platform: 'WALMART',
    //     amz_region: null,
    //     next_step: 'ACCOUNT-SELECTION',
    //     failure: null,
    //     accounts: [
    //       {
    //         account_id: 'amet lorem',
    //         name: 'tucan lorem',
    //         connected: 'NOT-CONNECTED',
    //       },
    //     ],
    //     account_id: 'sdasd',
    //     customer_id: 2022030064,
    //   },
    //   meta: {},
    // });

    return $.pipe(
      map((response: any) => {
        this.store = {
          status: response.data.next_step,
          failure: response.data.failure,
          data: response.data,
        };
        return this.store;
      }),
      catchError((error: any) => {
        if (error.code === 404) {
          this.store = { status: environment.connectApp.status.CREATE_USER, data: null };
          return throwError(404);
        } else {
          this.store = { status: environment.connectApp.status.ERROR, data: error };
          this.logger.logError('Could not signup data', error, 'error');
          return throwError({ msg: 'Could not signup data', error });
        }
      }),
    );
  }

  getCustomerData(customerId: string): Observable<{ customer?: Customer; summary?: CustomerSummary }> {
    return forkJoin([
      this.getCustomer(customerId),
      this.http.get<{ data: CustomerSummary }>(`${environment.app.resources.dashboard}customers/${customerId}/summary`),
    ]).pipe(
      map(([customer, summary]) => ({
        customer,
        summary: valueToCamelCase(summary.data),
      })),
      catchError(() => of({})),
    );
  }

  getCustomer(customerId: string): Observable<Customer> {
    return this.http.get<{ data: Customer }>(`${environment.app.resources.dashboard}customers/${customerId}`).pipe(
      map((customer) => valueToCamelCase(customer.data)),
      catchError(() => of({})),
    );
  }

  getCustomerSubscription(): Observable<CustomerSubscription> {
    return this.cs
      .getCustomerSubscription()
      .pipe(catchError(() => this.cs.startCustomerSubscription(AdspertPlanType.Essential, null, true)))
      .pipe(
        map((response) => response.data),
        catchError(() => of(null)),
      );
  }

  postData(data: any, captcha = ''): Observable<any> {
    const headers = {};
    if (captcha) headers['X-Adspert-Recaptcha-Token'] = captcha;

    return this.http.post(this.resourceUrl, data, { headers }).pipe(
      map((response: any) => response.data),
      catchError((error: any) => {
        this.logger.logError('creation failed', error, 'error');
        return throwError({ msg: 'creation failed', error });
      }),
    );
  }

  patchData(data: any): Observable<boolean> {
    return this.http.patch(this.resourceUrl, data).pipe(
      map(() => true),
      catchError((error: any) => {
        this.logger.logError('Updating failed', error, 'error');
        return throwError({ msg: 'Updating failed', error });
      }),
    );
  }
}
